import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Modal,
  Spin,
  message,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import { getCodigosPostales, getEstados, getMunicipios } from 'api/catalogos';

const SearchDireccion = ({
  callback,
  setVisible,
  open,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      setLoading(true);
      const _estados = await getEstados();
      if (mounted) {
        setEstados(_estados);
        setLoading(false);
      }
    };
    fetchData();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const changeState = async (estado) => {
    setLoading(true);
    form.setFieldsValue({
      estado,
      municipio: undefined,
    });
    const _estados = await getMunicipios({ estado });
    setMunicipios(_estados);
    setLoading(false);
  };

  const rules = { required: [{ required: true }] };

  const columns = [
    {
      titleText: 'Código',
      dataIndex: 'd_codigo',
      key: 'd_codigo',
      render: (text, record) => (
        <Button
          type="link"
          className="primary-text no-color"
          onClick={() => callback(record)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      ),
    },
    {
      titleText: 'Asentamiento',
      dataIndex: 'd_asenta',
      key: 'd_asenta',
    },
    {
      titleText: 'Tipo de Asentamiento',
      dataIndex: 'd_tipo_asenta',
      key: 'd_tipo_asenta',
    },
    {
      titleText: 'Municipio',
      dataIndex: 'nom_mun',
      key: 'nom_mun',
    },
    {
      titleText: 'Estado',
      dataIndex: 'nombre_de_AGEE',
      key: 'nombre_de_AGEE',
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onFinishSearch = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const params = form.getFieldsValue();
      const _codigosPostales = (await getCodigosPostales(params)).map((item) => ({
        ...item,
        nom_mun: item.municipio.nom_mun,
        nombre_de_AGEE: item.municipio.estado.nombre_de_AGEE,
      }));
      setData(_codigosPostales);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onSelectCodigoPostal = () => {
    const [key] = selectedRowKeys;
    if (key) {
      const match = data.find((e) => e.id === key);
      callback(match);
      setVisible(false);
    } else {
      message.warn('Seleccione un elemento');
    }
  };

  const onValuesChange = async (changedValues) => {
    const {
      pais,
      estado,
    } = changedValues;
    if (pais >= 0) {
      // TODO: soporte para paises
    } else if (estado >= 0) {
      await changeState(estado);
    }
  };

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setData();
  };

  return (
    <Modal
      width="90%"
      open={open}
      closable={false}
      maskClosable={!loading}
      keyboard={!loading}
      footer={null}
      title={(
        <FormSubmitControls
          label="Búsqueda Código Postal"
          onFinish={onSelectCodigoPostal}
          onCancel={onCancel}
          loading={loading}
        />
      )}
      onCancel={onCancel}
      forceRender
      centered
    >
      <Spin tip="Cargando..." spinning={loading}>
        <Form
          name="search_address"
          layout="vertical"
          form={form}
          initialValues={{ pais: 'México' }}
          onValuesChange={onValuesChange}
          onFinish={onFinishSearch}
          scrollToFirstError
        >
          <Row gutter={10}>
            <Col md={6} sm={24} xs={24}>
              <Form.Item
                label="Estado"
                name="estado"
                rules={rules.required}
              >
                <Select dataSource={estados} labelProp="nombre_de_AGEE" />
              </Form.Item>
            </Col>

            <Col md={6} sm={24} xs={24}>
              <Form.Item
                label="Municipio"
                name="municipio"
                rules={rules.required}
              >
                <Select dataSource={municipios} labelProp="nom_mun" />
              </Form.Item>
            </Col>

            <Col md={6} sm={24} xs={24}>
              <Form.Item
                label="Asentamiento/Colonia"
                name="d_asenta"
              >
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col md={6} sm={24} xs={24}>
              <Form.Item>
                <Button
                  type="link"
                  htmlType="submit"
                  style={{ marginTop: 20 }}
                  className="primary-on-hover"
                >
                  <SearchOutlined />
                  {' '}
                  Buscar
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
      <Table
        cols={columns}
        data={data}
        noControls
        allowSort={false}
        allowSearch={false}
        rowSelection={rowSelection}
        onRow={(record) => ({
          onDoubleClick: () => callback(record),
          onClick: () => {
            setSelectedRowKeys([record.id]);
          },
        })}
      />
    </Modal>
  );
};

SearchDireccion.propTypes = {
  callback: PropTypes.func.isRequired,
  open: PropTypes.bool,
  setVisible: PropTypes.func.isRequired,
};

SearchDireccion.defaultProps = {
  open: false,
};

export default SearchDireccion;
